import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, CheckCircle, WarningCircle, ArrowLeft, CircleNotch } from "@phosphor-icons/react";

const NONE = "__none__";
const UPLOAD_CHUNK = 512 * 1024; // 512 Ko bruts par requête → évite le rejet d'un gros POST par le proxy

// Encode un Uint8Array en base64 par blocs (efficace pour les gros fichiers).
function bytesToB64(bytes) {
  let binary = "";
  const sub = 0x8000;
  for (let i = 0; i < bytes.length; i += sub) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + sub));
  }
  return btoa(binary);
}

// Upload chunké d'un fichier vers le backend, puis analyse. Renvoie {sessionId, analysis}.
async function uploadFileInChunks(file, onProgress) {
  const { data: init } = await api.post("/admin/import/contacts/upload/init");
  const sessionId = init.session_id;
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let offset = 0;
  onProgress?.(0);
  while (offset < bytes.length) {
    const slice = bytes.subarray(offset, offset + UPLOAD_CHUNK);
    await api.post("/admin/import/contacts/upload/chunk", { session_id: sessionId, data: bytesToB64(slice) });
    offset += slice.length;
    onProgress?.(Math.min(99, Math.round((offset / bytes.length) * 100)));
  }
  const { data: analysis } = await api.post("/admin/import/contacts/upload/complete", { session_id: sessionId });
  onProgress?.(100);
  return { sessionId, analysis };
}

export default function ContactImportWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [sessionId, setSessionId] = useState("");
  const [pasted, setPasted] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileInput = useRef(null);

  const reset = () => {
    setStep(1); setSessionId(""); setPasted(""); setAnalysis(null); setMapping({}); setHasHeader(false); setProgress(null); setUploadPct(null);
  };
  const close = (v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); };

  const analyze = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/contacts/analyze", payload);
      setAnalysis(data);
      setMapping(data.suggested || {});
      setHasHeader(!!data.has_header);
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossible d'analyser le CSV");
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploadPct(0);
    try {
      const { sessionId: sid, analysis: a } = await uploadFileInChunks(file, setUploadPct);
      setSessionId(sid); setPasted("");
      setAnalysis(a);
      setMapping(a.suggested || {});
      setHasHeader(!!a.has_header);
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec du chargement du fichier");
    } finally {
      setBusy(false); setUploadPct(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const analyzePasted = async () => {
    if (!pasted.trim()) { toast.error("Collez le contenu CSV"); return; }
    setSessionId(""); await analyze({ csv: pasted });
  };

  const toggleHeader = async () => {
    const next = !hasHeader;
    setHasHeader(next);
    await analyze({ ...(sessionId ? { session_id: sessionId } : { csv: pasted }), has_header: next });
  };

  const setField = (field, colIdx) => {
    setMapping((m) => {
      const copy = { ...m };
      // retire ce champ de toute autre colonne, puis assigne
      Object.keys(copy).forEach((k) => { if (copy[k] === colIdx) delete copy[k]; });
      if (colIdx === NONE) delete copy[field];
      else copy[field] = Number(colIdx);
      return copy;
    });
  };

  const pollStatus = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/import/contacts/status/${jobId}`);
      setProgress(data);
      if (data.status === "running") {
        setTimeout(() => pollStatus(jobId), 1000);
      } else {
        setBusy(false);
        if (data.status === "done") {
          const extra = [
            data.updated ? `${data.updated} maj` : "",
            data.companies_created ? `${data.companies_created} entreprise(s) créée(s)` : "",
            data.error_count ? `${data.error_count} ignorée(s)` : "",
          ].filter(Boolean).join(" · ");
          toast.success(`${data.created} contact(s) importé(s)${extra ? ` · ${extra}` : ""}`);
          onDone?.();
        } else {
          toast.error(`Import échoué : ${data.message || "erreur inconnue"}`);
        }
      }
    } catch (e) {
      setBusy(false);
      toast.error("Suivi de l'import interrompu — l'import se poursuit en arrière-plan.");
    }
  };

  const runImport = async () => {
    const hasName = mapping.name != null || (mapping.first_name != null && mapping.last_name != null);
    if (!hasName) { toast.error("Mappez « Nom complet » (ou « Prénom » + « Nom de famille »)."); return; }
    if (mapping.company_name == null && mapping.company_domain == null) {
      toast.error("Mappez au moins « Entreprise (nom) » ou « Entreprise (domaine) »."); return;
    }
    setBusy(true);
    try {
      const payload = { mapping, has_header: hasHeader, ...(sessionId ? { session_id: sessionId } : { csv: pasted }) };
      const { data } = await api.post("/admin/import/contacts", payload);
      setProgress({ status: "running", processed: 0, total: data.total, created: 0, updated: 0, companies_created: 0, error_count: 0 });
      setStep(3);
      pollStatus(data.job_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const colLabel = (i) => (analysis?.columns?.[i] ?? `Colonne ${i + 1}`);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-surface border-border max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="contact-import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UploadSimple size={20} className="text-accent" /> Importer des décideurs (CSV)
          </DialogTitle>
          <DialogDescription className="text-muted">
            {step === 1 ? "Chargez votre fichier — l'encodage (UTF-8 / Windows) et le séparateur sont gérés automatiquement."
              : step === 2 ? "Vérifiez la correspondance des colonnes puis lancez l'import."
              : "Import en cours — vous pouvez fermer cette fenêtre, il se poursuit en arrière-plan."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div
              onClick={() => { if (!busy) fileInput.current?.click(); }}
              className={`border-2 border-dashed border-border rounded-lg p-8 text-center transition-colors ${busy ? "opacity-70 cursor-wait" : "cursor-pointer hover:border-accent/50"}`}
              data-testid="import-dropzone">
              <UploadSimple size={32} className="text-accent mx-auto mb-2" weight="duotone" />
              <div className="text-fg font-medium">Choisir un fichier CSV</div>
              <div className="text-xs text-muted mt-1">Colonnes attendues : nom, fonction, email, téléphone, LinkedIn, entreprise, domaine, catégorie…</div>
              <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="wizard-file-input" />
            </div>
            {uploadPct !== null && (
              <div data-testid="upload-progress">
                <div className="flex items-center justify-between text-xs text-muted mb-1.5">
                  <span className="flex items-center gap-2"><CircleNotch size={14} className="text-accent animate-spin" /> Chargement du fichier…</span>
                  <span className="font-mono text-accent">{uploadPct}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-surface-2 overflow-hidden">
                  <div className="h-full bg-accent rounded-full transition-all duration-300" style={{ width: `${Math.max(4, uploadPct)}%` }} data-testid="upload-progress-bar" />
                </div>
              </div>
            )}
            <div className="text-center text-xs text-muted">— ou —</div>
            <textarea
              value={pasted} onChange={(e) => setPasted(e.target.value)} data-testid="wizard-paste"
              placeholder="Collez le contenu CSV ici…"
              className="w-full min-h-[120px] bg-surface-2 border border-border rounded-md p-3 text-fg font-mono text-xs" />
            <button onClick={analyzePasted} disabled={busy || !pasted.trim()} data-testid="wizard-analyze-paste"
              className="w-full bg-surface-2 border border-border text-fg font-semibold px-4 py-2.5 rounded-md hover:border-accent/40 disabled:opacity-50">
              {busy ? "Analyse…" : "Analyser le texte collé"}
            </button>
          </div>
        )}

        {step === 2 && analysis && (
          <div className="space-y-5 py-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-muted">
                <span className="text-fg font-semibold">{analysis.total}</span> lignes détectées ·
                séparateur <code className="text-accent">{analysis.delimiter === "," ? "virgule" : analysis.delimiter}</code>
              </div>
              <label className="flex items-center gap-2 text-sm text-fg/85 cursor-pointer" data-testid="wizard-has-header">
                <input type="checkbox" checked={hasHeader} onChange={toggleHeader} className="accent-[#00E5FF]" />
                La première ligne est un en-tête
              </label>
            </div>

            {/* Mapping */}
            <div>
              <div className="overline mb-2">Correspondance des colonnes</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {analysis.fields.map((f) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <span className="text-sm text-fg w-44 shrink-0">
                      {f.label}{f.required && <span className="text-accent"> *</span>}
                    </span>
                    <Select value={mapping[f.key] != null ? String(mapping[f.key]) : NONE} onValueChange={(v) => setField(f.key, v)}>
                      <SelectTrigger className="bg-surface-2 border-border text-fg h-9" data-testid={`map-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— Ignorer —</SelectItem>
                        {analysis.columns.map((c, i) => <SelectItem key={i} value={String(i)}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div>
              <div className="overline mb-2">Aperçu ({analysis.sample.length} premières lignes)</div>
              <div className="surface rounded-lg overflow-x-auto border border-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {analysis.columns.map((c, i) => {
                        const mapped = Object.entries(mapping).find(([, v]) => v === i);
                        return (
                          <th key={i} className="px-3 py-2 whitespace-nowrap">
                            <div className="text-muted font-mono">{c}</div>
                            {mapped ? <div className="text-accent text-[10px] font-semibold mt-0.5">→ {analysis.fields.find((f) => f.key === mapped[0])?.label}</div>
                              : <div className="text-muted/40 text-[10px] mt-0.5">non mappé</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.sample.map((row, ri) => (
                      <tr key={ri} className="border-b border-border/50" data-testid={`preview-row-${ri}`}>
                        {row.map((cell, ci) => <td key={ci} className="px-3 py-2 text-fg/80 whitespace-nowrap max-w-[220px] truncate">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-muted hover:text-fg px-3 py-2">
                <ArrowLeft size={15} /> Changer de fichier
              </button>
              <button onClick={runImport} disabled={busy} data-testid="wizard-import-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60">
                {busy ? "Import en cours…" : <><CheckCircle size={16} weight="fill" /> Importer {analysis.total} décideur(s)</>}
              </button>
            </div>
            {mapping.name == null && !(mapping.first_name != null && mapping.last_name != null) && (
              <div className="flex items-center gap-2 text-xs text-amber-400"><WarningCircle size={14} /> Mappez « Nom complet » (ou « Prénom » + « Nom de famille »).</div>
            )}
          </div>
        )}

        {step === 3 && progress && (
          <div className="space-y-5 py-4" data-testid="import-progress">
            {progress.status === "running" ? (
              <>
                <div className="flex items-center gap-3">
                  <CircleNotch size={22} className="text-accent animate-spin" />
                  <div>
                    <div className="text-fg font-display font-semibold">Import en cours…</div>
                    <div className="text-xs text-muted">Traitement par lots — grands fichiers gérés sans blocage.</div>
                  </div>
                </div>
                {(() => {
                  const pct = progress.total ? Math.round((progress.processed / progress.total) * 100) : 0;
                  return (
                    <div>
                      <div className="flex items-center justify-between text-xs text-muted mb-1.5">
                        <span data-testid="import-progress-label">{progress.processed} / {progress.total} lignes</span>
                        <span className="font-mono text-accent">{pct}%</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-surface-2 overflow-hidden">
                        <div className="h-full bg-accent rounded-full transition-all duration-500" style={{ width: `${Math.max(4, pct)}%` }} data-testid="import-progress-bar" />
                      </div>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <Stat label="Créés" value={progress.created} />
                  <Stat label="Mis à jour" value={progress.updated} />
                  <Stat label="Entreprises" value={progress.companies_created} />
                </div>
              </>
            ) : progress.status === "done" ? (
              <>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="fill" className="text-emerald-400" />
                  <div className="text-fg font-display font-semibold">Import terminé ✅</div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <Stat label="Contacts créés" value={progress.created} />
                  <Stat label="Mis à jour" value={progress.updated} />
                  <Stat label="Entreprises créées" value={progress.companies_created} />
                </div>
                {progress.error_count > 0 && (
                  <div className="text-xs text-amber-400 flex items-center gap-2" data-testid="import-errors">
                    <WarningCircle size={14} /> {progress.error_count} ligne(s) ignorée(s) (nom ou entreprise manquant).
                  </div>
                )}
                <button onClick={() => close(false)} data-testid="import-done-close"
                  className="w-full bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift">
                  Fermer
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <WarningCircle size={24} weight="fill" className="text-red-400" />
                  <div className="text-fg font-display font-semibold">Import échoué</div>
                </div>
                <div className="text-sm text-muted">{progress.message || "Une erreur est survenue."}</div>
                <button onClick={() => setStep(2)} className="w-full bg-surface-2 border border-border text-fg font-semibold px-5 py-2.5 rounded-md">
                  Revenir au mapping
                </button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface rounded-lg border border-border py-3">
      <div className="text-2xl font-display font-bold text-fg">{value ?? 0}</div>
      <div className="text-[11px] text-muted mt-0.5">{label}</div>
    </div>
  );
}
